'use client';

import Link from 'next/link';
import { useEffect } from 'react';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Log error details in development
    if (process.env.NODE_ENV === 'development') {
    }

    // Optional: Send error to error tracking service
    // reportError(error);
  }, [error]);

  // Extract useful error information
  const errorInfo = {
    message: error.message || 'An unexpected error occurred',
    name: error.name,
    stack: error.stack?.split('\n'),
    time: new Date().toISOString(),
    // Parse location from stack trace if available
    location: error.stack?.split('\n')[1]?.trim()?.split('at ')[1] || 'Unknown location',
    digest: error.digest,
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white p-8 shadow-xl">
        <div className="mb-6 text-center">
          <h2 className="mb-4 text-2xl font-bold text-red-600">Something went wrong!</h2>
          <p className="text-gray-700">We&apos;re sorry for the inconvenience. Please try again.</p>
        </div>

        {/* Show detailed error information in development */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-6 rounded-md bg-gray-50 p-4">
            <h3 className="mb-2 font-medium text-gray-900">Error Details:</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong>Message:</strong> {errorInfo.message}
              </p>
              <p>
                <strong>Type:</strong> {errorInfo.name}
              </p>
              <p>
                <strong>Time:</strong> {errorInfo.time}
              </p>
              <p>
                <strong>Location:</strong> {errorInfo.location}
              </p>
              {errorInfo.digest && (
                <p>
                  <strong>Digest:</strong> {errorInfo.digest}
                </p>
              )}
              <div className="mt-2">
                <p className="mb-1 font-medium text-gray-900">Stack Trace:</p>
                <pre className="max-h-40 overflow-auto rounded bg-gray-100 p-2 text-xs">
                  {errorInfo.stack?.join('\n')}
                </pre>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-center space-x-4">
          <button
            onClick={() => reset()}
            className="rounded-md bg-red-600 px-6 py-2 text-base font-medium text-white transition-colors hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md border border-gray-300 bg-white px-6 py-2 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none"
          >
            Reload page
          </button>
          <Link href="/">
            <button className="rounded-md border border-gray-300 bg-white px-6 py-2 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none">
              Home
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
